import { useQuery } from '@apollo/client';
import { Spinner } from '@fluentui/react-components';
import React from 'react';
import momentUtc from '../../utils/DateFormatter';
import { MILESTONE_DETAILS } from '../../utils/statApi/MilestoneApi';
import StatDateFormats from '../../utils/types/DateFormats';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import { labelStyles } from '../../app/common/styles/CommonStyleObjects';

export interface IMilestoneDetailsProps {
  id: number;
  status: string;
  onDataLoaded?: (text: string) => void;
}

export const getMilestoneStatusFormatted = (rawStatus: string): string => {
  if (rawStatus === 'NOTSTARTED') {
    return 'Not Started';
  }
  if (rawStatus === 'COMPLETE') {
    return 'Complete';
  }
  return 'Ready';
};

const MilestoneDetails = ({ id, status, onDataLoaded }: IMilestoneDetailsProps): JSX.Element => {
  const labelClasses = labelStyles();
  const { data, loading, error } = useQuery(MILESTONE_DETAILS, { variables: { milestoneId: id } });

  const milestoneDetails = data?.milestoneDetails;

  React.useEffect(() => {
    if (!loading && !error && milestoneDetails && onDataLoaded) {
      const accessibleText = `
        ${milestoneDetails.title}.
        Due Date ${momentUtc(milestoneDetails.dueDate, StatDateFormats.DayBreakout)}.
        Status ${getMilestoneStatusFormatted(milestoneDetails.status)}.
        ${milestoneDetails.actionText ?? ''}
      `;

      onDataLoaded(accessibleText);
    }
  }, [loading, error, milestoneDetails, onDataLoaded]);

  if (loading && !error) {
    return <Spinner label="Loading" />;
  }

  return (
    <div aria-live="polite">
      <LoadingErrorMessage loading={loading} error={error} />
      {!loading && !error && milestoneDetails && (
        <>
          <strong>{milestoneDetails.title}</strong>
          <br />
          <br />
          <span className={labelClasses.root}>Due Date:</span>{' '}
          {momentUtc(milestoneDetails.dueDate, StatDateFormats.DayBreakout)} <br />
          <span className={labelClasses.root}>Status:</span>{' '}
          {getMilestoneStatusFormatted(milestoneDetails.status)} <br />
          {milestoneDetails.actionText}
        </>
      )}
    </div>
  );
};

export default MilestoneDetails;
